// The footwear label card: markup and CSS exactly as the catalog label sheet
// has always printed them. Moved here unchanged so other designs can sit next
// to it in the template registry (see index.js).

import { escapeHTML } from "../html";

/** One label card. `barcodeSvg` is pre-rendered markup from barcodeRender.js. */
export const renderBarcodeLabelCard = (label, barcodeSvg, { attributes = "", className = "" } = {}) => `
    <article class="label-card${className ? ` ${className}` : ""}"${attributes}>
      <div class="label-card-inner">
        <section class="label-copy">
          <div class="label-product">${escapeHTML(
            label.productName || label.modelCode || "Catalog item"
          )}</div>
        </section>

        <section class="label-size-row">
          <div class="label-color-row">
            <span class="label-color-dot" style="background:${escapeHTML(label.colorTone)};"></span>
            <strong class="label-color-copy">${escapeHTML(label.color)}</strong>
          </div>
          <div class="label-size-block">${escapeHTML(label.size)}</div>
        </section>

        <section class="label-barcode-shell">
          <div class="label-barcode">${barcodeSvg}</div>
          <div class="label-barcode-text">${escapeHTML(label.barcode)}</div>
        </section>

        ${
          label.price
            ? `<div class="label-price">${escapeHTML(label.price)}</div>`
            : `<div class="label-price label-price--empty"></div>`
        }
      </div>
    </article>
  `;

export const BARCODE_LABEL_CARD_CSS = `
    .label-card {
      width: var(--label-width);
      height: var(--label-height);
      min-height: 0;
      min-width: 0;
      overflow: hidden;
      border: 0.32mm solid var(--soft-line);
      border-radius: 2.2mm;
      background: linear-gradient(180deg, #fdfcf9 0%, #ffffff 100%);
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .label-card-inner {
      height: 100%;
      padding: var(--padding-top) var(--padding-right) var(--padding-bottom) var(--padding-left);
      display: grid;
      grid-template-rows: auto auto 1fr auto;
      gap: 0.95mm;
    }

    .label-copy {
      min-width: 0;
      display: grid;
      gap: 0;
      align-content: start;
      margin-bottom: 5.2mm;
    }

    .label-product {
      font-size: var(--title-size);
      line-height: 1;
      font-weight: var(--title-weight);
      letter-spacing: 0.035em;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: var(--title-lines);
      max-height: calc(var(--title-lines) * var(--title-size));
      overflow: hidden;
      text-transform: uppercase;
      text-align: var(--title-align);
      text-overflow: var(--title-overflow);
    }

    .label-color-row {
      flex: 1 1 0;
      display: flex;
      align-items: baseline;
      justify-content: var(--color-justify);
      gap: 1.2mm;
      min-width: 0;
    }

    .label-color-dot {
      width: 2.6mm;
      height: 2.6mm;
      border-radius: 50%;
      border: 0.2mm solid rgba(17, 17, 17, 0.1);
      flex-shrink: 0;
      display: none;
    }

    .label-color-copy {
      min-width: 0;
      font-size: var(--color-size);
      line-height: 1;
      font-weight: var(--color-weight);
      text-align: var(--color-align);
      text-transform: uppercase;
    }

    .label-size-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1.8mm;
    }

    .label-size-block {
      display: block;
      flex: 1 1 0;
      text-align: var(--size-align);
      font-size: var(--size-size);
      line-height: 0.86;
      font-weight: var(--size-weight);
      letter-spacing: -0.08em;
    }

    .label-barcode-shell {
      display: grid;
      align-self: end;
      gap: var(--barcode-spacing);
      padding-top: 1mm;
      border-top: 0.24mm solid var(--soft-line);
    }

    .label-barcode {
      width: var(--barcode-width);
      margin-inline: auto;
      overflow: hidden;
      padding: 0 var(--barcode-side-pad);
    }

    .label-barcode svg {
      width: 100% !important;
      height: var(--barcode-height) !important;
      display: block;
    }

    .label-barcode-text {
      font-size: var(--barcode-text-size);
      line-height: 1.15;
      font-weight: 500;
      letter-spacing: 0.1em;
      text-align: center;
      word-break: break-all;
      color: rgba(17, 17, 17, 0.72);
      display: var(--barcode-text-display);
    }

    .label-price {
      min-height: 6pt;
      font-size: var(--price-size);
      line-height: 1;
      font-weight: var(--price-weight);
      letter-spacing: 0.08em;
      color: rgba(17, 17, 17, 0.72);
      text-align: var(--price-align);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .label-price--empty {
      visibility: hidden;
    }
`;

export const footwearClassic = Object.freeze({
  id: "footwear-classic",
  renderCard: renderBarcodeLabelCard,
  cardCss: BARCODE_LABEL_CARD_CSS,
});
